import { createInvitesBody, inviteResults, type GroupActionOutcome, type InviteResult, type Invitee } from './group-chat-actions';

/**
 * "New group" — a group started from the Chats screen rather than by converting a private chat. The flow is the
 * Add-people one with two differences: the name is always required (there is no chat yet to borrow a title from),
 * and there is no history choice (a brand-new chat has no history, so the batch goes out as `ALL`).
 *
 * Sequencing, shared with iOS / Android `NewGroupFlow`: create a fresh conversation exactly as New chat does, then
 * `CreateGroupInvites` on it. The conversation is created ONCE per dialog — a failed or unconfirmed invite batch
 * retries on the same id, so a flaky network never leaves a trail of empty chats. A dialog closed after the chat
 * was created but before it became a group deletes that empty chat (best effort).
 */

export const NEW_GROUP_MAX_PEOPLE = 10;

/** Create is offered only for a named group of 1..10 people. */
export function newGroupCreatable(name: string, invitees: readonly Invitee[]): boolean {
  return name.trim().length > 0 && invitees.length > 0 && invitees.length <= NEW_GROUP_MAX_PEOPLE;
}

export type NewGroupStep =
  /** The chat could not be created: nothing else happened. */
  | { kind: 'createFailed' }
  /** The chat exists, the invite batch failed — retry reuses `conversationId`. */
  | { kind: 'inviteFailed'; conversationId: string; outcome: GroupActionOutcome<Record<string, unknown>> }
  /** Some addresses have no Symphony account: ask once, then re-send on the same chat with `confirmUnknown`. */
  | { kind: 'confirmUnknown'; conversationId: string; emails: string[] }
  /** The group exists: one result per person. */
  | { kind: 'done'; conversationId: string; results: InviteResult[] };

export interface NewGroupDeps {
  /** Creates a fresh conversation the way New chat does; resolves its id, or null / throws on failure. */
  createConversation: () => Promise<string | null>;
  createInvites: (body: Record<string, unknown>) => Promise<GroupActionOutcome<Record<string, unknown>>>;
  /**
   * A FORCED `members` read of the new group that stores its summary where the chat looks (`loadGroupSummary`). The
   * flow awaits it before the results step with "Open group", so the chat opens as the group — its header, "Message
   * the group", names resolved — never as the private chat a list that has not re-read yet would draw. Its failure
   * does not fail the flow (the tap retries it).
   */
  loadSummary?: (conversationId: string) => Promise<unknown>;
}

export async function runNewGroup(
  input: { name: string; invitees: readonly Invitee[]; conversationId: string | null; confirmUnknown: boolean; setupCard?: { conversationId: string; messageId: string } | null },
  deps: NewGroupDeps,
): Promise<NewGroupStep> {
  let conversationId = input.conversationId;
  if (!conversationId) {
    try {
      conversationId = await deps.createConversation();
    } catch {
      conversationId = null;
    }
    if (!conversationId) return { kind: 'createFailed' };
  }
  const body = createInvitesBody({
    conversationId,
    invitees: [...input.invitees],
    policy: 'ALL',
    groupName: input.name,
    confirmUnknown: input.confirmUnknown,
    setupCard: input.setupCard ?? null,
  });
  if (!body) return { kind: 'inviteFailed', conversationId, outcome: { kind: 'failed', status: 0 } };
  const outcome = await deps.createInvites(body);
  if (outcome.kind !== 'ok') return { kind: 'inviteFailed', conversationId, outcome };
  if (outcome.data.requiresConfirmation === true && !input.confirmUnknown) {
    const emails = Array.isArray(outcome.data.unknownEmails) ? (outcome.data.unknownEmails as string[]) : [];
    return { kind: 'confirmUnknown', conversationId, emails };
  }
  if (deps.loadSummary) {
    try {
      await deps.loadSummary(conversationId);
    } catch {
      // "Open group" reads it again before it navigates.
    }
  }
  return { kind: 'done', conversationId, results: inviteResults(outcome.data.outcomes) };
}

/**
 * "Open group": navigate to the new group only once its summary is known — when none is held yet, await the read
 * first (`load`), then open. iOS / Android `NewGroupFlow.openGroup` do the same.
 */
export async function openNewGroup(conversationId: string, deps: { known: (id: string) => boolean; load: (id: string) => Promise<unknown>; open: (id: string) => void }): Promise<void> {
  if (!deps.known(conversationId)) {
    try {
      await deps.load(conversationId);
    } catch {
      // Open anyway: the chat re-reads the group itself.
    }
  }
  deps.open(conversationId);
}

/** The empty chat to delete when the dialog closes: one that was created and never became a group. */
export function abandonedConversation(conversationId: string | null, done: boolean): string | null {
  return conversationId && !done ? conversationId : null;
}

/** The Groups chip's own empty state ("No groups yet" + Start a group): only on a settled, unsearched, empty Groups list. */
export function groupsEmptyStateShown(input: {
  groupChatEnabled: boolean;
  groupsChipSelected: boolean;
  search: string;
  loading: boolean;
  error: boolean;
  rowCount: number;
  filling: boolean;
}): boolean {
  return input.groupChatEnabled && input.groupsChipSelected && !input.search.trim()
    && !input.loading && !input.error && !input.filling && input.rowCount === 0;
}
